import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  List,
  ListItemButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { SPEED } from '@src/constants/voice';
import { CancelRounded, CheckRounded } from '@mui/icons-material';
import { COLOR } from '@src/styles/color';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  StyledDrawer,
  StyledListItem,
  StyledSpeedDrawer,
} from './index.styled';

const SPEED_LIMIT = {
  MIN: 0.25,
  MAX: 1.9,
};

const SpeedDrawer = ({ open, onClose, onChange, speedValue }) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { synthesisRequest } = useSelector((state) => state.synthesisRequest);
  const { user } = useSelector((state) => state.auth);

  const [inputValue, setInputValue] = useState(speedValue || '');

  const useCustomSpeedInput = getFeatureValue(FEATURE_KEYS.CUSTOM_SPEED_INPUT, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const handleSelectedValue = (value) => {
    if (onChange) onChange('speed', value);
    setInputValue(value);
    onClose();
  };

  const handleInputSubmit = () => {
    const value = parseFloat(inputValue);
    if (value < SPEED_LIMIT.MIN || value > SPEED_LIMIT.MAX) return;
    handleSelectedValue(value);
  };

  const handleInputChange = (event) => {
    let { value } = event.target;
    value = value.replace(',', '.');
    const isValid = /^\d*\.?\d{0,2}$/.test(value);

    if (isValid) {
      setInputValue(value);
    }
  };

  useEffect(() => {
    if (speedValue) {
      setInputValue(speedValue);
    }
  }, [speedValue]);

  return (
    <StyledDrawer
      title={t('speed')}
      anchor="bottom"
      open={open}
      onClose={onClose}
    >
      <Box className="drawer-title">
        <span className="title">{t('speed')}</span>
        <CancelRounded sx={{ color: COLOR.darkV2[32] }} onClick={onClose} />
      </Box>
      <StyledSpeedDrawer
        minValue={SPEED_LIMIT.MIN}
        maxValue={SPEED_LIMIT.MAX}
        inputValue={inputValue}
      >
        <List>
          {SPEED.map((item) => (
            <StyledListItem
              key={item.value}
              className="list-item"
              isSelected={synthesisRequest.speed === item.value}
            >
              <ListItemButton
                onClick={() => {
                  handleSelectedValue(item.value);
                }}
              >
                <div className="show-speed">
                  <div className="speed-value">{item.value}x</div>
                  <div className="speed-title">{t(item.title)}</div>
                </div>
              </ListItemButton>
            </StyledListItem>
          ))}
        </List>
        {useCustomSpeedInput && (
          <Box className="speed-input-container">
            <TextField
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleInputSubmit();
                }
              }}
              placeholder={t('enterOtherSpeedReading', {
                min: SPEED_LIMIT.MIN,
                max: SPEED_LIMIT.MAX,
              })}
              className="speed-input"
              InputProps={{
                inputProps: { min: 0, max: 2, step: 0.01, lang: 'en' },
                endAdornment: inputValue && (
                  <InputAdornment position="end">
                    <CheckRounded
                      className="icon-choose-speed-input"
                      onClick={handleInputSubmit}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </StyledSpeedDrawer>
    </StyledDrawer>
  );
};

export default SpeedDrawer;
